import cn from 'classnames';
import DotsLoading from '../dotsLoading';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;

  isLoading?: boolean;

  variant?: 'primary' | 'secondary' | 'outline' | 'errorOutline' | 'custom';

  customVariantClassName?: string;

  size?: 'small' | 'medium' | 'large';

  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;

  className?: string;

  baseStyles?: string;
}

const Button = ({
  children,
  isLoading = false,
  variant = 'primary',
  size = 'medium',
  leftIcon,
  rightIcon,
  className,
  customVariantClassName,
  baseStyles = 'flex flex-1 items-center justify-center rounded-lg font-medium transition-colors outline-none text-md min-h-[48px]',
  ...props
}: ButtonProps) => {
  const variantStyles = {
    primary: 'bg-[#518A15] text-white',
    secondary: 'bg-farm-shop-200 text-farm-shop-800',
    outline: 'bg-white border-[1px] border-farm-shop-500 text-farm-shop-500',
    errorOutline: 'bg-white border-[1px] border-red-500 text-red-500',
    custom: '', // handled separately
  };

  const sizeStyles = {
    small: 'h-8 px-3 text-sm',
    medium: 'h-12 px-4',
    large: 'h-14 px-6 text-lg',
  };

  const disabledStyles =
    'opacity-60 bg-gray-500 cursor-not-allowed pointer-events-none';

  const isDisabled = isLoading || props.disabled;

  const variantClass =
    variant === 'custom'
      ? customVariantClassName ?? ''
      : variantStyles[variant];

  return (
    <button
      {...props}
      disabled={isDisabled}
      className={cn(
        baseStyles,
        variantClass,
        sizeStyles[size],
        isDisabled && disabledStyles,
        className,
      )}
    >
      {isLoading ? (
        <DotsLoading />
      ) : (
        <>
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2">{rightIcon}</span>}
        </>
      )}
    </button>
  );
};

export default Button;
